import React, { useState } from 'react';
import { TravelAbilityWordmark } from './TravelAbilityLogo';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Users, Target, TrendingUp, ArrowRight, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LeadCapture = ({ onComplete, pendingResults }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.organization.trim()) newErrors.organization = 'Organization is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/leads`, formData);
      const leadId = response.data.id;

      // Submit assessment data now that we have a lead ID
      if (pendingResults) {
        try {
          await axios.post(`${API}/assessment/submit`, {
            lead_id: leadId,
            answers: pendingResults.answers,
            profile: pendingResults.profile.id
          });
        } catch (e) { console.error('Error submitting assessment:', e); }
      }

      onComplete({
        ...formData,
        leadId
      });
    } catch (error) {
      console.error('Error creating lead:', error);
      setErrors({ submit: 'Failed to submit. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <div className="min-h-screen bg-[#111111] flex flex-col">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1633098272154-b97f6e147b00?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxhY2Nlc3NpYmxlJTIwdHJhdmVsJTIwd2hlZWxjaGFpciUyMGF1dGhlbnRpY3xlbnwwfHx8fDE3NzU5MjE2NzB8MA&ixlib=rb-4.1.0&q=85)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#111111]/50 via-[#111111]/80 to-[#111111]" />
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-8">
          <div className="flex justify-center mb-8">
            <TravelAbilityWordmark />
          </div>
          
          <h1 
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-center text-[#F3F4F6] mb-4"
            data-testid="hero-title"
          >
            Interactive DMO Accessibility Challenge
          </h1>
          <p className="text-lg sm:text-xl text-center text-[#FBBF24] font-semibold mb-4">
            Are You Future-Proof?
          </p>
          <p className="text-base sm:text-lg text-center text-[#9CA3AF] max-w-2xl mx-auto">
            This survey helps you determine if your destination is prepared for the 
            historic demographic shift of 2030 while mapping out your next steps.
          </p>
        </div>
      </div>

      {/* Stats Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="bg-[#1A1A1A] border-[#333333]">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 rounded-xl bg-[#3B82F6]/20">
                <Users className="w-6 h-6 text-[#3B82F6]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#F3F4F6]">73M</p>
                <p className="text-sm text-[#9CA3AF]">Baby Boomers 65+ by 2030</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#1A1A1A] border-[#333333]">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 rounded-xl bg-[#10B981]/20">
                <TrendingUp className="w-6 h-6 text-[#10B981]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#F3F4F6]">$100B</p>
                <p className="text-sm text-[#9CA3AF]">Accessible Travel Market</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#1A1A1A] border-[#333333]">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 rounded-xl bg-[#FBBF24]/20">
                <Target className="w-6 h-6 text-[#FBBF24]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#F3F4F6]">18%</p>
                <p className="text-sm text-[#9CA3AF]">Travel Parties Need Accessibility</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Form Section */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-8">
        <Card className="w-full max-w-md bg-[#1A1A1A] border-2 border-[#333333]">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl text-[#F3F4F6]">Almost Done!</CardTitle>
            <CardDescription className="text-[#9CA3AF]">
              Enter your details to see your personalized results
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-[#F3F4F6] text-base">
                  Full Name
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Jane Smith"
                  value={formData.name}
                  onChange={handleChange}
                  className="bg-[#111111] border-[#333333] text-[#F3F4F6] text-lg h-12 focus:border-[#FBBF24] focus:ring-[#FBBF24]"
                  data-testid="input-name"
                />
                {errors.name && (
                  <p className="text-[#EF4444] text-sm">{errors.name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#F3F4F6] text-base">
                  Email Address
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="jane@destination.org"
                  value={formData.email}
                  onChange={handleChange}
                  className="bg-[#111111] border-[#333333] text-[#F3F4F6] text-lg h-12 focus:border-[#FBBF24] focus:ring-[#FBBF24]"
                  data-testid="input-email"
                />
                {errors.email && (
                  <p className="text-[#EF4444] text-sm">{errors.email}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="organization" className="text-[#F3F4F6] text-base">
                  Organization / DMO
                </Label>
                <Input
                  id="organization"
                  name="organization"
                  type="text"
                  placeholder="Visit Somewhere Tourism"
                  value={formData.organization}
                  onChange={handleChange}
                  className="bg-[#111111] border-[#333333] text-[#F3F4F6] text-lg h-12 focus:border-[#FBBF24] focus:ring-[#FBBF24]"
                  data-testid="input-organization"
                />
                {errors.organization && (
                  <p className="text-[#EF4444] text-sm">{errors.organization}</p>
                )}
              </div>

              {errors.submit && (
                <p className="text-[#EF4444] text-sm text-center">{errors.submit}</p>
              )}

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#FBBF24] hover:bg-[#F59E0B] text-[#111111] font-bold text-lg h-14 rounded-xl"
                data-testid="start-challenge-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    See My Results
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <footer className="py-6 px-4 text-center">
      </footer>
    </div>
  );
};

export default LeadCapture;
